/**
 * Part 16 — Resolve the active status set for an entity type.
 *
 * Returns the org's default set's statuses if defined, else the canonical
 * fallback. Cached at module-level so multiple consumers don't refetch.
 *
 * Usage:
 *   const { options, loading } = useStatusOptions('project')
 *   options.map(o => <option value={o.key}>{o.label}</option>)
 */
import { useEffect, useState } from 'react'

import {
  canonicalOptionsFor,
  statusSetsService,
  type OrgStatus,
  type StatusEntityType,
} from '../services/statusSets'

interface CacheEntry {
  fetchedAt: number
  options: OrgStatus[]
}

const CACHE_TTL_MS = 60_000
const cache: Record<StatusEntityType, CacheEntry | undefined> = {
  project: undefined,
  task: undefined,
}

// One in-flight promise per entity_type to dedupe concurrent fetches.
const inflight: Record<StatusEntityType, Promise<OrgStatus[]> | undefined> = {
  project: undefined,
  task: undefined,
}

/**
 * Force a refresh on the next call — used after the admin saves a status set
 * so other components see the change without a page reload.
 */
export function invalidateStatusOptions(entityType?: StatusEntityType) {
  if (entityType) {
    cache[entityType] = undefined
    return
  }
  cache.project = undefined
  cache.task = undefined
}

async function fetchOptions(entityType: StatusEntityType): Promise<OrgStatus[]> {
  const sets = await statusSetsService.list({ entity_type: entityType })
  // Prefer the default; otherwise use the first active set; else canonical.
  const chosen =
    sets.find((s) => s.is_default) ?? sets[0] ?? null
  if (!chosen || !chosen.statuses?.length) {
    return canonicalOptionsFor(entityType)
  }
  // Sort by position so admins can drag-to-reorder later.
  return [...chosen.statuses].sort((a, b) => a.position - b.position)
}

async function loadEntityType(entityType: StatusEntityType): Promise<OrgStatus[]> {
  const cached = cache[entityType]
  const fresh = cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS
  if (fresh) return cached.options

  if (inflight[entityType]) {
    return inflight[entityType] as Promise<OrgStatus[]>
  }

  const p = fetchOptions(entityType)
    .then((options) => {
      cache[entityType] = { fetchedAt: Date.now(), options }
      return options
    })
    .catch(() => {
      // Failure → use canonical fallback. We don't cache failures so the
      // next consumer retries.
      return canonicalOptionsFor(entityType)
    })
    .finally(() => {
      inflight[entityType] = undefined
    })
  inflight[entityType] = p
  return p
}

export interface UseStatusOptionsResult {
  options: OrgStatus[]
  loading: boolean
}

export function useStatusOptions(
  entityType: StatusEntityType,
): UseStatusOptionsResult {
  const [options, setOptions] = useState<OrgStatus[]>(() =>
    cache[entityType]?.options ?? canonicalOptionsFor(entityType),
  )
  const [loading, setLoading] = useState<boolean>(!cache[entityType])

  useEffect(() => {
    let cancelled = false
    setLoading(!cache[entityType])
    loadEntityType(entityType)
      .then((opts) => {
        if (cancelled) return
        setOptions(opts)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [entityType])

  return { options, loading }
}
